'use client'

import { memo, useCallback, useEffect, useId, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { CRAFT_MEDIA } from './craft-media'

type CraftAppLayout = 'default' | 'whatsapp' | 'gmail' | 'shell'

type CraftApp = {
  id: string
  name: string
  accent: string
  accentSoft: string
  chrome: string
  spoken: string
  typed: string
  context: string
  layout: CraftAppLayout
  /** baby: Short line under the stamp in the rail */
  stamp: string
  /** baby: Staged craft brand sticker */
  icon: string
}

const APPS: readonly CraftApp[] = [
  {
    id: 'gmail',
    name: 'Gmail',
    accent: '#c4785a',
    accentSoft: 'rgba(196, 120, 90, 0.14)',
    chrome: 'Inbox · draft',
    spoken: '“find unread from finance and draft a reply…”',
    typed: 'Draft ready: 3 threads summarized locally. Approve to send?',
    context: 'Mail agent',
    layout: 'gmail',
    stamp: 'Mail',
    icon: CRAFT_MEDIA.brands.gmail,
  },
  {
    id: 'slack',
    name: 'Slack',
    accent: '#5b4a6a',
    accentSoft: 'rgba(91, 74, 106, 0.12)',
    chrome: '#ops',
    spoken: '“summarize #ops and post the action items…”',
    typed: 'Action items: ship checklist · owner: you · due today.',
    context: 'Team chat',
    layout: 'default',
    stamp: 'Chat',
    icon: CRAFT_MEDIA.brands.slack,
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    accent: '#3d8f6e',
    accentSoft: 'rgba(61, 143, 110, 0.14)',
    chrome: 'Chat',
    spoken: '“reply that the report is on its way…”',
    typed: 'Report is on its way: drafted on-device.',
    context: 'Messages',
    layout: 'whatsapp',
    stamp: 'Msg',
    icon: CRAFT_MEDIA.brands.whatsapp,
  },
  {
    id: 'docs',
    name: 'Docs',
    accent: '#21728d',
    accentSoft: 'rgba(33, 114, 141, 0.12)',
    chrome: 'Brief',
    spoken: '“turn the meeting notes into a one-pager…”',
    typed: 'One-pager: goals, decisions, owners: generated locally.',
    context: 'Writing',
    layout: 'default',
    stamp: 'Write',
    icon: CRAFT_MEDIA.brands.docs,
  },
  {
    id: 'shell',
    name: 'Shell',
    accent: '#5f7380',
    accentSoft: 'rgba(95, 115, 128, 0.14)',
    chrome: 'Terminal',
    spoken: '“list today’s git commits on this repo…”',
    typed: '$ git log --since=midnight: ran with your approval',
    context: 'Local tools',
    layout: 'shell',
    stamp: 'CLI',
    icon: CRAFT_MEDIA.brands.shell,
  },
  {
    id: 'browser',
    name: 'Browser',
    accent: '#1a5f75',
    accentSoft: 'rgba(26, 95, 117, 0.14)',
    chrome: 'Agent tab',
    spoken: '“open the dashboard and capture the status…”',
    typed: 'Status captured from the open tab: stayed on your machine.',
    context: 'Browser agent',
    layout: 'default',
    stamp: 'Web',
    icon: CRAFT_MEDIA.brands.browser,
  },
] as const

const MODES = [
  { id: 'comms', label: 'Comms', hint: 'Gmail, Slack, WhatsApp: with approval' },
  { id: 'docs', label: 'Docs & brief', hint: 'Write where files already live' },
  { id: 'tools', label: 'Shell & browser', hint: 'Local tools, not a cloud copilot' },
] as const

const CYCLE_MS = 5600

const TypeLine = memo(function TypeLine({
  text,
  accent,
  reduceMotion,
}: {
  text: string
  accent: string
  reduceMotion: boolean
}) {
  if (reduceMotion) {
    return (
      <span>
        {text}
        <span
          className="craft-works__caret"
          style={{ backgroundColor: accent }}
          aria-hidden
        />
      </span>
    )
  }

  // baby: Word-level reveal (not per-char) so CSS never grid-scatters letters.
  const words = text.split(/(\s+)/)

  return (
    <span className="craft-works__type">
      {words.map((word, index) => (
        <motion.span
          key={`${text}-${index}`}
          className="craft-works__type-char"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.12 + index * 0.04, duration: 0.12 }}
        >
          {word}
        </motion.span>
      ))}
      <motion.span
        className="craft-works__caret"
        style={{ backgroundColor: accent }}
        animate={{ opacity: [1, 0.15, 1] }}
        transition={{ duration: 0.85, repeat: Infinity }}
        aria-hidden
      />
    </span>
  )
})

const SceneChrome = memo(function SceneChrome({
  app,
  reduceMotion,
}: {
  app: CraftApp
  reduceMotion: boolean
}) {
  return (
    <div className="craft-works__scene" style={{ ['--cw-accent' as string]: app.accent }}>
      <div className="craft-works__scene-wash" style={{ background: app.accentSoft }} aria-hidden />
      <div className="craft-works__scene-bar">
        <img
          src={app.icon}
          alt=""
          aria-hidden
          className="craft-works__scene-icon"
          draggable={false}
        />
        <span className="craft-works__scene-name">{app.name}</span>
        <span className="craft-works__scene-chrome">{app.chrome}</span>
        <span className="craft-works__scene-badge">{app.context}</span>
      </div>

      <div className="craft-works__scene-body">
        {app.layout === 'whatsapp' ? (
          <div className="craft-works__bubbles">
            <div className="craft-works__bubble craft-works__bubble--in">hey: any update?</div>
            <div
              className="craft-works__bubble craft-works__bubble--out"
              style={{ backgroundColor: app.accent }}
            >
              <TypeLine text={app.typed} accent="#fff" reduceMotion={reduceMotion} />
            </div>
          </div>
        ) : null}

        {app.layout === 'gmail' ? (
          <div className="craft-works__mail">
            <p>
              <span className="craft-works__mail-label">To</span> client@…
            </p>
            <p>
              <span className="craft-works__mail-label">Subject</span> Follow-up
            </p>
            <div className="craft-works__mail-body">
              <TypeLine text={app.typed} accent={app.accent} reduceMotion={reduceMotion} />
            </div>
          </div>
        ) : null}

        {app.layout === 'shell' ? (
          <div className="craft-works__shell">
            <p className="craft-works__shell-prompt">$ ninx · local session</p>
            <p>
              <TypeLine text={app.typed} accent={app.accent} reduceMotion={reduceMotion} />
            </p>
          </div>
        ) : null}

        {app.layout === 'default' ? (
          <div className="craft-works__plain">
            <TypeLine text={app.typed} accent={app.accent} reduceMotion={reduceMotion} />
          </div>
        ) : null}
      </div>

      <div className="craft-works__scene-foot">
        <img
          src={CRAFT_MEDIA.brands.ninx}
          alt=""
          aria-hidden
          className="craft-works__scene-ninx"
          draggable={false}
        />
        NINX · local session
      </div>
    </div>
  )
})

/**
 * baby: Craft Works In: papercraft desk stage, twine rail, soft scene crossfade.
 */
export const CraftWorksIn = memo(function CraftWorksIn() {
  const reduceMotion = Boolean(useReducedMotion())
  const [activeId, setActiveId] = useState(APPS[0]!.id)
  const [paused, setPaused] = useState(false)
  const twineId = useId().replace(/:/g, '')
  const active = APPS.find((app) => app.id === activeId) ?? APPS[0]!
  const activeIndex = Math.max(
    0,
    APPS.findIndex((app) => app.id === activeId),
  )

  useEffect(() => {
    if (reduceMotion || paused) return
    const id = window.setInterval(() => {
      setActiveId((prev) => {
        const index = APPS.findIndex((app) => app.id === prev)
        return APPS[(index + 1) % APPS.length]!.id
      })
    }, CYCLE_MS)
    return () => window.clearInterval(id)
  }, [reduceMotion, paused])

  const selectApp = useCallback((id: string) => {
    setPaused(true)
    setActiveId(id)
  }, [])

  // baby: Twine draw progress 0→1 across the rail nodes.
  const twineProgress = APPS.length <= 1 ? 1 : activeIndex / (APPS.length - 1)

  return (
    <div className="craft-works" data-testid="craft-works-in">
      <div className="craft-works__intro">
        <p className="craft-section__eyebrow">Works in</p>
        <h2 className="craft-section__title">Works where your work already lives.</h2>
        <p className="craft-section__body">
          Gmail, Slack, WhatsApp, Docs, shell, and browser. NINX runs locally, asks before tools
          fire, and keeps sessions on your machine.
        </p>
      </div>

      <div className="craft-works__modes" aria-label="Work modes">
        {MODES.map((mode) => (
          <article key={mode.id} className="craft-works__mode">
            <h3>{mode.label}</h3>
            <p>{mode.hint}</p>
          </article>
        ))}
      </div>

      {/* baby: Paper stamp rail + twine: one control surface (no duplicate footer grid). */}
      <div className="craft-works__rail-wrap">
        <svg className="craft-works__twine" viewBox="0 0 1000 48" aria-hidden>
          <defs>
            <linearGradient id={`cw-twine-${twineId}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#21728d" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#21728d" stopOpacity="0.55" />
            </linearGradient>
          </defs>
          <path
            d="M 40 28 C 180 8, 320 48, 500 24 S 820 8, 960 28"
            fill="none"
            stroke={`url(#cw-twine-${twineId})`}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="1000"
            strokeDashoffset={1000 * (1 - twineProgress)}
            className="craft-works__twine-draw"
          />
          <path
            d="M 40 28 C 180 8, 320 48, 500 24 S 820 8, 960 28"
            fill="none"
            stroke="rgba(196,164,132,0.35)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray="6 10"
          />
        </svg>

        <div className="craft-works__rail" role="tablist" aria-label="Apps and tools where NINX works">
          {APPS.map((app, index) => {
            const isActive = app.id === activeId
            return (
              <button
                key={app.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => selectApp(app.id)}
                className={cn('craft-works__stamp', isActive && 'is-active')}
                style={{
                  ['--cw-accent' as string]: app.accent,
                  animationDelay: `${index * 40}ms`,
                }}
              >
                <span className="craft-works__stamp-tape" aria-hidden />
                <img
                  src={app.icon}
                  alt=""
                  aria-hidden
                  className="craft-works__stamp-icon"
                  draggable={false}
                />
                <span className="craft-works__stamp-name">{app.name}</span>
                <span className="craft-works__stamp-meta">{app.stamp}</span>
              </button>
            )
          })}
        </div>

        {!reduceMotion && !paused ? (
          <div className="craft-works__progress" aria-hidden>
            <motion.span
              key={activeId}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: CYCLE_MS / 1000, ease: 'linear' }}
            />
          </div>
        ) : null}
      </div>

      <div className="craft-works__stage">
        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            className="craft-works__stage-grid"
            initial={reduceMotion ? false : { opacity: 0, y: 14, rotate: -0.4 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: -10, rotate: 0.4 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
          >
            <SceneChrome app={active} reduceMotion={reduceMotion} />

            <aside className="craft-works__brief">
              <div className="craft-works__brief-block">
                <p className="craft-works__brief-label">You ask</p>
                <p className="craft-works__brief-copy">{active.spoken}</p>
              </div>
              <div className="craft-works__brief-block craft-works__brief-block--act">
                <p className="craft-works__brief-label">It acts · on device</p>
                <p className="craft-works__brief-copy craft-works__brief-copy--ink">
                  NINX works inside {active.name} with local tools and your approval: data stays on
                  your desktop by default.
                </p>
              </div>
              <div className="craft-works__tags">
                {['Local', 'Approval', 'On-device'].map((tag) => (
                  <span key={tag} className="craft-chip">
                    {tag}
                  </span>
                ))}
              </div>
            </aside>
          </motion.div>
        </AnimatePresence>
      </div>

      <p className="craft-works__footnote">Chat · mail · docs · shell · browser · your approval</p>
    </div>
  )
})

CraftWorksIn.displayName = 'CraftWorksIn'
